import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ArrowLeft, Crown, Info, ServerCrash, Unlink } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { GuildBreadcrumb, GuildSidebar } from "@/components/guilds";
import { GuildLayoutSkeleton } from "@/components/guilds/guild-layout-skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LockedGuildBanner } from "@/components/subscription/locked-guild-banner";
import { useGuildDetails } from "@/hooks/use-guild-details";
import { useGuildWS } from "@/hooks/use-websocket";
import { NotificationBell } from "@/components/notifications";
import { UserMenu } from "@/components/user-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { useSubscription } from "@/lib/api/subscription";
import { useGuildRoute } from "@/hooks/use-guild-route";
import { dashboardMeQueryOptions } from "@/lib/api/dashboard";
import { guildPermissionsMeQueryOptions } from "@/lib/api/guilds";
import type { GuildPermissions } from "@/lib/api/types";
import { getDiscordIconUrl } from "@/utils/discord";

export const Route = createFileRoute("/dashboard/guilds/$applicationGuildId")({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const meData = await queryClient.ensureQueryData(dashboardMeQueryOptions);
    const applications = meData?.data?.applications ?? [];

    let app = undefined;
    let appGuild = undefined;
    for (const a of applications) {
      const g = a.guilds.find((g) => g.id === params.applicationGuildId);
      if (g) {
        app = a;
        appGuild = g;
        break;
      }
    }

    const tenantId = appGuild?.tenantId ?? null;
    const applicationId = app?.id ?? null;

    let guildPermissions: GuildPermissions = {
      tickets: "view",
      knowledgeBase: "view",
      modmail: "view",
      threadsQA: "view",
      guildSettings: "view",
    };
    let guildUserRole: "owner" | "admin" | "member" = "member";

    if (appGuild) {
      try {
        const permData = await queryClient.ensureQueryData(
          guildPermissionsMeQueryOptions(params.applicationGuildId)
        );
        if (permData?.data) {
          guildPermissions = permData.data.permissions;
          guildUserRole = permData.data.role;
        }
      } catch {
        // Fallback to default view permissions
      }
    }

    return {
      tenantId,
      applicationId,
      isCustomBot: app?.isCustom ?? false,
      isLocked: appGuild?.isLocked ?? false,
      lockedReason: appGuild?.lockedReason ?? null,
      applicationGuildId: appGuild?.id ?? null,
      discordGuildId: appGuild?.discordGuildId ?? null,
      applicationName: app?.name ?? null,
      guildPermissions,
      guildUserRole,
    };
  },
  component: GuildLayout,
});

function GuildLayout() {
  const { applicationGuildId } = Route.useParams();
  const { isLocked, lockedReason } = Route.useRouteContext();
  const { discordGuild, enrichedGuild, applications, isMember, isPending } =
    useGuildDetails(applicationGuildId);
  const { session, tenantId } = useGuildRoute();
  useGuildWS(applicationGuildId);
  const { data: subscription } = useSubscription(tenantId!, applicationGuildId);
  const isFreePlan = !subscription?.data || subscription.data.plan === "free";
  const planName = subscription?.data?.planName ?? "Free";

  if (isPending) {
    return <GuildLayoutSkeleton />;
  }

  // Resolve guild data: prefer Discord data, fall back to enriched app data
  const resolvedGuild = discordGuild
    ? {
        id: discordGuild.id,
        name: discordGuild.name,
        icon: discordGuild.icon,
        isOwner: discordGuild.isOwner,
      }
    : enrichedGuild;
  const isNonMember = !isMember && !!resolvedGuild;

  if (!resolvedGuild) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="relative flex max-w-md flex-col items-center text-center">
          <div className="pointer-events-none absolute -top-20 left-1/2 h-[300px] w-[400px] -translate-x-1/2 rounded-full bg-rose-500/5 blur-3xl" />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative mb-8 flex items-center gap-2"
          >
            <img src="/BLDR.png" alt="BLDR" className="size-7 rounded-lg" />
            <span className="font-bold text-rose-500 dark:text-rose-400">
              BLDR
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            className="relative mb-6 flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500/10 to-red-500/10 ring-1 ring-rose-500/10"
          >
            <ServerCrash className="size-10 text-rose-500/80" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
            className="relative mb-2 text-balance text-xl font-semibold"
          >
            Server not found
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
            className="text-muted-foreground relative mb-8 text-pretty text-sm"
          >
            This server doesn&apos;t exist or you don&apos;t have permission to
            access it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
          >
            <Button
              asChild
              className="gap-2 bg-gradient-to-r from-rose-500 to-red-500 text-white hover:opacity-90"
            >
              <Link to="/dashboard">
                <ArrowLeft className="size-4" />
                Back to Dashboard
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!applicationGuildId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="relative flex max-w-md flex-col items-center text-center">
          <div className="pointer-events-none absolute -top-20 left-1/2 h-[300px] w-[400px] -translate-x-1/2 rounded-full bg-rose-500/5 blur-3xl" />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative mb-8 flex items-center gap-2"
          >
            <img src="/BLDR.png" alt="BLDR" className="size-7 rounded-lg" />
            <span className="font-bold text-rose-500 dark:text-rose-400">
              BLDR
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            className="relative mb-6"
          >
            <Avatar className="ring-border size-20 rounded-2xl ring-1">
              <AvatarImage
                src={getDiscordIconUrl(resolvedGuild.id, resolvedGuild.icon)}
                alt={resolvedGuild.name}
              />
              <AvatarFallback className="rounded-2xl bg-gradient-to-br from-rose-500/10 to-red-500/10 text-2xl">
                {resolvedGuild.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="bg-background ring-background absolute -bottom-1 -right-1 flex size-8 items-center justify-center rounded-full ring-2">
              <div className="flex size-6 items-center justify-center rounded-full bg-gradient-to-br from-rose-500/15 to-red-500/15">
                <Unlink className="size-3.5 text-rose-500" />
              </div>
            </div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
            className="relative mb-2 text-balance text-xl font-semibold"
          >
            {resolvedGuild.name}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
            className="text-muted-foreground relative mb-8 text-pretty text-sm"
          >
            This server is not connected to any application and cannot be
            managed from here.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
          >
            <Button
              asChild
              className="gap-2 bg-gradient-to-r from-rose-500 to-red-500 text-white hover:opacity-90"
            >
              <Link to="/dashboard">
                <ArrowLeft className="size-4" />
                Back to Dashboard
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const guild = resolvedGuild;

  const seen = new Set<string>();
  const connectedGuilds: {
    id: string;
    discordGuildId: string;
    name: string;
    icon: string | null;
  }[] = [];
  for (const app of applications) {
    for (const ag of app.guilds) {
      if (seen.has(ag.id)) continue;
      seen.add(ag.id);
      connectedGuilds.push({
        id: ag.id,
        discordGuildId: ag.discordGuildId,
        name: ag.discordGuildName || ag.discordGuildId,
        icon: ag.discordGuildIcon,
      });
    }
  }

  return (
    <SidebarProvider>
      <GuildSidebar
        guild={guild}
        guilds={connectedGuilds}
        applications={applications}
      />
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="min-w-0 flex-1">
            <GuildBreadcrumb guildName={guild.name} />
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {tenantId &&
              (isFreePlan ? (
                <Link
                  to="/dashboard/teams/$tenantId"
                  params={{ tenantId }}
                  search={{ tab: "subscription" }}
                  className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-rose-500 to-red-500 px-3 py-1 text-xs font-medium text-white shadow-sm transition-opacity hover:opacity-90"
                >
                  <Crown className="size-3" />
                  Upgrade to Pro
                </Link>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs"
                  asChild
                >
                  <Link
                    to="/dashboard/teams/$tenantId"
                    params={{ tenantId }}
                    search={{ tab: "subscription" }}
                  >
                    <Crown className="size-3.5 text-rose-500" />
                    <span>{planName}</span>
                  </Link>
                </Button>
              ))}
            <NotificationBell />
            <ThemeToggle />
            <UserMenu user={session.user} />
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6 pb-24">
          <div className="mx-auto max-w-5xl space-y-6">
            {isNonMember && (
              <Alert>
                <Info className="size-4" />
                <AlertDescription>
                  You are not a member of this Discord server. Join the server
                  to fully manage it.
                </AlertDescription>
              </Alert>
            )}
            {isLocked && <LockedGuildBanner lockedReason={lockedReason} />}
            <Outlet />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
